import React, { useContext, useMemo } from 'react'

import { useCachedData } from 'hooks/useCachedData'
import { usePollApi } from 'hooks/usePollApi'
import { Market } from 'types/Market'
import { Loadable, catLoadables, mapLoadable, mkLoadable } from 'utils/loadable'

type MarketsContextType = Loadable<{
  markets: Market[]
  marketsById: MarketsById
  activeMarkets: Market[]
}>
const MarketsContext = React.createContext<MarketsContextType>({
  loading: true,
})

function assignName(rawMarket: Omit<Market, 'name'>): Market {
  const market: Market = { ...rawMarket, name: '' }
  market.name = `${market.exchange} ${market.symbol}`
  return market
}

type MarketsById = { [k: number]: Market }

export function MarketsProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const markets = useCachedData(
    'contexts:Markets',
    mapLoadable(
      mkLoadable(
        usePollApi<Market>(
          {
            path: 'markets',
            params: new URLSearchParams({
              select: '*,symbol',
              order: 'volume.desc.nullslast,exchange,symbol',
            }),
          },
          60000,
        ),
      ),
      markets => markets.map(assignName),
    ),
    // The markets response is far too large to store in localStorage, so we just
    // store the top 200 markets by volume (which the API response is already
    // ordered by)
    markets => markets.slice(0, 200),
  )

  const activeMarkets = useMemo(
    () =>
      mapLoadable(markets, data => data.filter(m => !m.disabled && !m.expired)),
    [markets],
  )

  const marketsById = useMemo(
    () =>
      mapLoadable<MarketsById, Market[]>(markets, markets =>
        Object.fromEntries(markets.map(m => [m.id, m])),
      ),
    [markets],
  )

  const value = catLoadables({
    markets,
    activeMarkets,
    marketsById,
  })

  return (
    <MarketsContext.Provider value={value}>{children}</MarketsContext.Provider>
  )
}

export const useMarket = (
  id: number | undefined,
): Loadable<Market | undefined> =>
  mapLoadable(useContext(MarketsContext), data =>
    typeof id === 'undefined' ? undefined : data.marketsById[id],
  )

export const useMarketByName = (
  exchange: string,
  asset: string,
  currency: string | undefined,
): Loadable<Market | undefined> =>
  mapLoadable(useContext(MarketsContext), data => {
    const sym = currency ? `${asset}/${currency}` : asset
    const market = data.markets.find(
      m => m.exchange === exchange && m.symbol === sym,
    )
    return market
  })

export const useMarkets = (): Loadable<Market[]> => {
  const ctx = useContext(MarketsContext)
  return useMemo(() => mapLoadable(ctx, data => data.markets), [ctx])
}

export const useMarketsById = (): Loadable<MarketsById> => {
  const ctx = useContext(MarketsContext)
  return useMemo(() => mapLoadable(ctx, data => data.marketsById), [ctx])
}

export const useActiveMarkets = (): Loadable<Market[]> => {
  const ctx = useContext(MarketsContext)
  return useMemo(() => mapLoadable(ctx, data => data.activeMarkets), [ctx])
}
